import type { ChartData, Point } from "chart.js";
import { formatNumber } from "helpers/utils";
import { t } from "i18next";

import type { DataType } from "../utils";
import { isCurrencyDataType } from "../utils";

/**
 * Chart component related utils
 */
export type DataPoint = { label: string; value: number };
export type DataSet = ChartData["datasets"][0] & { dataOthers: DataPoint[] };
export type PieChartData = ChartData<"pie", number[], unknown> & {
    datasets: DataSet[];
};
export type BarChartData = ChartData<"bar", (number | [number, number] | null)[], unknown> & {
    datasets: DataSet[];
};
export type LineChartData = ChartData<"line", (number | Point | null)[], unknown> & {
    datasets: DataSet[];
};

const formatMetricValue = (value: number, dataType: DataType) =>
    isCurrencyDataType(dataType) ? formatNumber(value, { isCurrency: true }) : `${value} ${t("Analytics.Copies")}`;

/**
 * Format tooltip label (when hover the data) used for BarChart and PieChart
 * Show "quantity" or "€"
 * @param context label context prop from chart-js
 * @param dataType Type of data for chart Y-axis
 * @param dataOthers Data values for tooltip "Other"
 */
export const tooltipLabelFormat = (context: any, dataType: DataType, dataOthers?: DataPoint[]) => {
    const label = context?.label || "";
    const value = context.raw;

    const hasOtherData = label === t("Analytics.Others") && dataOthers?.length;
    const labelLines = [] as string[];

    if (hasOtherData) {
        const othersInfo = dataOthers.map((item) => `${item.label}: ${formatMetricValue(item.value, dataType)}`);
        labelLines.push(...othersInfo);
    }

    if (value !== null && !hasOtherData) {
        labelLines.push(formatMetricValue(value, dataType));
    }

    return { label, labelLines };
};
